/** Indian mobile: 10 digits, starting with 6–9 */

export const INDIAN_MOBILE_LENGTH = 10;

export const PHONE_FORM_FIELDS = new Set([
  "phone",
  "contactNumber",
  "phone_number",
  "vendor_mobile",
  "phoneNumber",
]);

export const isPhoneFormField = (field) => PHONE_FORM_FIELDS.has(field);

export const sanitizeFormFieldValue = (field, value) =>
  isPhoneFormField(field) ? sanitizeIndianMobileInput(value) : value;

export const sanitizeIndianMobileInput = (value, maxLength = INDIAN_MOBILE_LENGTH) => {
  let digits = String(value ?? "").replace(/\D/g, "");

  if (digits.length > maxLength) {
    if (digits.startsWith("91") && digits.length >= 12) {
      digits = digits.slice(2);
    } else if (digits.startsWith("0") && digits.length === 11) {
      digits = digits.slice(1);
    }
  }

  return digits.slice(0, maxLength);
};

export const validateIndianMobile = (value) => {
  const digits = sanitizeIndianMobileInput(value);

  if (!digits) {
    return {
      isValid: false,
      error: "Phone number is required",
      digits: "",
    };
  }

  if (digits.length !== INDIAN_MOBILE_LENGTH) {
    return {
      isValid: false,
      error: "Please enter a valid 10-digit mobile number",
      digits,
    };
  }

  if (!/^[6-9]\d{9}$/.test(digits)) {
    return {
      isValid: false,
      error: "Please enter a valid 10-digit mobile number",
      digits,
    };
  }

  return { isValid: true, error: "", digits };
};

/** Contact field: digits and optional leading + (international). */
export const sanitizeContactInput = (value, { indianOnly = false } = {}) => {
  if (indianOnly) {
    return sanitizeIndianMobileInput(value);
  }
  const raw = String(value ?? "");
  const hasPlus = raw.trim().startsWith("+");
  const digits = raw.replace(/\D/g, "").slice(0, 15);
  if (!digits) return hasPlus ? "+" : "";
  return hasPlus ? `+${digits}` : digits;
};

/** Indian 10-digit or international (7–15 digits, optional +). */
export const validateContactPhone = (value, { indianOnly = false } = {}) => {
  if (indianOnly) {
    const indian = validateIndianMobile(value);
    return {
      isValid: indian.isValid,
      error: indian.error,
      contact: indian.digits || "",
      digits: indian.digits,
    };
  }

  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return { isValid: false, error: "Phone number is required", contact: "" };
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) {
    return {
      isValid: false,
      error: "Please enter a valid phone number with country code",
      contact: "",
    };
  }

  const contact = trimmed.startsWith("+") ? `+${digits}` : digits;
  return { isValid: true, error: "", contact };
};

/** Wrap a field onChange so only digits are stored (max 10). */
export const withIndianMobileInput =
  (onChange) =>
  (event) => {
    if (!onChange) return;
    const sanitized = sanitizeIndianMobileInput(event?.target?.value ?? "");
    onChange({
      ...event,
      target: { ...event.target, value: sanitized },
    });
  };
